// ============================================================================
// Componente: FinanceiroAutomacoes.js
// Objetivo: Central de automações dentro do Módulo Financeiro
// Padrão visual: Financeiro / Verde-azulado
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Settings,
    ArrowRight,
    Lock,
    CreditCard,
    QrCode,
    Send,
    Sliders,
    Landmark,
    Zap,
    CheckCircle2
} from 'lucide-react';



import AutomacaoConciliacaoAdyen from './FinanceiroAutomacaoConciliacaoAdyen';
import AutomacaoConferenciaPIX from './FinanceiroAutomacaoConferenciaPIX';
import AutomacaoFaturaCartao from './FinanceiroAutomacaoFaturaCartao';
import AutomacaoImpostosFederais from './FinanceiroAutomacaoImpostosFederais';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const FinanceiroAutomacoes = () => {
    const [activeSubTab, setActiveSubTab] = useState('visao-geral');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const perfilUsuario = perfilNoModulo(user?.perfil, 'financeiro') || '';

    const canAcessarFinanceiro = [
        'Financeiro',
        'Gerente Adm',
        'Admin',
        'Diretoria',
        'Auditoria'
    ].includes(perfilUsuario);

    const corPrincipal = '#16a39c';
    const corPrincipalEscura = '#0f766e';
    const corFundoClaro = '#e0f2f1';
    const corFundoMaisClaro = '#f0fdfa';

    const tabStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        backgroundColor: isActive ? corFundoClaro : 'transparent',
        color: isActive ? corPrincipal : '#64748b',
        border: isActive ? `1px solid ${corPrincipal}` : '1px solid transparent',
        borderRadius: '8px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '14px'
    });

    const renderSubTab = () => {
        switch (activeSubTab) {
            case 'adyen':
                return <AutomacaoConciliacaoAdyen />;

            case 'cartao':
                return <AutomacaoFaturaCartao />;

            case 'pix':
                return <AutomacaoConferenciaPIX />;

            case 'impostos-federais':
                return <AutomacaoImpostosFederais />;

            case 'visao-geral':
            default:
                return <VisaoGeralAutomacoes />;
        }
    };

    const mudarAba = (aba, permitido = true) => {
        if (!permitido) return;
        setActiveSubTab(aba);
    };

    const AutomationCard = ({
        id,
        title,
        desc,
        icon,
        tag,
        isAllowed = true,
        disabled = false
    }) => {
        const permitido = isAllowed && !disabled;

        return (
            <div
                onClick={() => mudarAba(id, permitido)}
                style={{
                    backgroundColor: permitido ? '#fff' : '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '24px',
                    cursor: permitido ? 'pointer' : 'not-allowed',
                    opacity: permitido ? 1 : 0.65,
                    minHeight: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 20px -8px rgba(22, 163, 156, 0.35)';
                    e.currentTarget.style.borderColor = corPrincipal;
                }}
                onMouseLeave={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                }}
            >
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: permitido ? corPrincipal : '#cbd5e1'
                }} />

                {!permitido && (
                    <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        color: '#94a3b8'
                    }}>
                        <Lock size={20} />
                    </div>
                )}

                <div>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '14px',
                        backgroundColor: permitido ? corFundoMaisClaro : '#f1f5f9',
                        color: permitido ? corPrincipal : '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '18px'
                    }}>
                        {icon}
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '19px',
                            fontWeight: '800',
                            color: '#0f172a'
                        }}>
                            {title}
                        </h3>

                        {tag && (
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '800',
                                color: corPrincipalEscura,
                                backgroundColor: corFundoClaro,
                                borderRadius: '999px',
                                padding: '4px 8px'
                            }}>
                                {tag}
                            </span>
                        )}
                    </div>

                    <p style={{
                        margin: 0,
                        color: '#64748b',
                        fontSize: '14px',
                        lineHeight: '1.6'
                    }}>
                        {desc}
                    </p>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '22px',
                    color: permitido ? corPrincipal : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '800'
                }}>
                    {permitido ? (
                        <>
                            <ArrowRight size={16} />
                            Acessar automação
                        </>
                    ) : (
                        <>
                            <Lock size={16} />
                            {disabled ? 'Em implantação' : 'Acesso bloqueado'}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const VisaoGeralAutomacoes = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '20px'
            }}>
                <AutomationCard
                    id="adyen"
                    title="Conciliação Adyen"
                    tag="Adquirente"
                    desc="Automatiza a leitura, filtragem e conciliação financeira dos relatórios da Adyen."
                    icon={
                        <img
                            src="/adyen-logo.png"
                            alt="Adyen"
                            style={{
                                width: '38px',
                                height: '38px',
                                objectFit: 'contain'
                            }}
                        />
                    }
                    isAllowed={canAcessarFinanceiro}
                />

                <AutomationCard
                    id="cartao"
                    title="Faturas Cartão Corporativo"
                    tag="PDF / Excel"
                    desc="Extrai e organiza faturas em PDF, categorizando transações por colaborador."
                    icon={<CreditCard size={30} />}
                    isAllowed={canAcessarFinanceiro}
                />

                <AutomationCard
                    id="pix"
                    title="Conferência PIX"
                    tag="Conciliação"
                    desc="Cruza arquivos de vendas PIX da ATOS com extratos bancários para conferência."
                    icon={<QrCode size={30} />}
                    isAllowed={canAcessarFinanceiro}
                />

                <AutomationCard
                    id="impostos-federais"
                    title="Cálculo dos Impostos Federais"
                    tag="Folha / SAP"
                    desc="Transforma a planilha de folha (INSS, IRRF e FGTS) no cálculo do mês, com os blocos de lançamento para o SAP."
                    icon={<Landmark size={30} />}
                    isAllowed={canAcessarFinanceiro}
                />

            </div>
        </div>
    );

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
        }}>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    borderBottom: '1px solid #e2e8f0',
                    paddingBottom: '16px'
                }}>
                    <button
                        onClick={() => setActiveSubTab('visao-geral')}
                        style={tabStyle(activeSubTab === 'visao-geral')}
                    >
                        <CheckCircle2 size={18} />
                        Visão Geral
                    </button>

                    <button
                        onClick={() => setActiveSubTab('impostos-federais')}
                        style={tabStyle(activeSubTab === 'impostos-federais')}
                    >
                        <Landmark size={18} />
                        Impostos Federais
                    </button>

                    <button
                        onClick={() => setActiveSubTab('adyen')}
                        style={tabStyle(activeSubTab === 'adyen')}
                    >
                        <Send size={18} />
                        Conciliação Adyen
                    </button>

                    <button
                        onClick={() => setActiveSubTab('cartao')}
                        style={tabStyle(activeSubTab === 'cartao')}
                    >
                        <CreditCard size={18} />
                        Faturas Cartão
                    </button>

                    <button
                        onClick={() => setActiveSubTab('pix')}
                        style={tabStyle(activeSubTab === 'pix')}
                    >
                        <QrCode size={18} />
                        Conferência PIX
                    </button>

                </div>

            <div style={{ paddingTop: '8px' }}>
                {renderSubTab()}
            </div>
        </div>
    );
};

export default FinanceiroAutomacoes;